<template>
  <div class="debug-parameter-settings-container">
    <!-- 连接状态提示区域 -->
    <div v-if="isReconnecting" class="connection-status">
      <el-alert
        :title="reconnectMessage"
        type="warning"
        :closable="false"
        show-icon
      />
    </div>
    <div class="content-wrapper">
      <div class="group-container">
        <div v-for="group in groupedPaginatedData" :key="group.groupName" class="parameter-group">
          <div class="group-title">{{ group.groupName }}</div>
          <div class="data-grid">
            <div class="data-card" v-for="(param, index) in group.parameters" :key="index">
              <!-- 优化后的标题区域 -->
              <div class="card-header">
                <div class="title-section">
                  <div class="main-title">
                    <span class="param-title">{{ param.title }}</span>
                    <span v-if="param.varName && param.varName !== param.title" class="param-varname">
                      ({{ param.varName }})
                    </span>
                  </div>                  
                  <el-tooltip
                    v-if="param.description"
                    effect="dark"
                    :content="param.description"
                    placement="top-start"
                    :show-after="300"
                    popper-class="custom-description-tooltip"
                  >
                    <div class="param-description">
                      <span class="description-text">{{ param.description }}</span>
                      <i class="el-icon-question description-icon"></i>
                    </div>
                  </el-tooltip>
                </div>
                <!-- <div class="time-section">
                  <div class="update-time">
                    <i class="el-icon-time"></i>
                    {{ param.updateAt }}
                  </div>
                </div> -->
              </div>
              
              <!-- 优化后的数值和控制区域 -->
              <div class="value-control-section">
                <div class="current-value-display">
                  <div class="value-label">{{ t("message.ClientDeviceMonitorSystemDebugHostDebug.currentValue") }}</div>
                  <div class="value-content">
                    <span class="value-number">{{ param.value }}</span>
                    <span class="value-unit">{{ param.unit }}</span>
                  </div>
                  <div class="time-section">
                  <div class="update-time">
                    <i class="el-icon-time"></i>
                    {{ param.updateAt }}
                  </div>
                </div>
                </div>
                
                <div class="set-value-control">
                  <div class="set-value-label">{{ t("message.ClientDeviceMonitorSystemDebugHostDebug.setValue") }}</div>
                  <div class="control-row">
                    <el-input-number
                      v-model="param.setValue"
                      :min="param.min"
                      :max="param.max"
                      :step="param.step"
                      :precision="getPrecision(param.step)"
                      size="small"
                      controls-position="right"
                      @change="handleValueChange(param)"
                      class="set-value-input"
                    />
                    <el-button 
                      type="primary" 
                      size="small" 
                      @click="handleSetValue(param)"
                      :loading="param.isSetting"
                      :disabled="!isConnected"
                      class="set-button"
                    >
                      <i class="el-icon-s-promotion"></i>
                      {{ t("message.ClientDeviceMonitorSystemDebugHostDebug.send") }}
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页区域保持不变 -->
      <div class="pagination-container">
        <div class="pagination-actions">
          <div class="selector-wrapper">
            <ClientProjectVariableSelector           
              v-model="selectedVariables"    
              :projectId="currentProjectId"
              :collectionId="currentCollectionId"
              :domainMenuId="currentDomainMenuId"
              :domainId="currentDomainId" 
              @change="handleVariableChange"
              class="variable-selector"
            />
          </div>
          <el-button 
            type="primary" 
            :icon="Refresh" 
            @click="handleQueryDataClick"
            :loading="isQeuryLoading"            
            class="refresh-btn"
          >
            {{ t("message.ClientProjectMonitorSystemMonitorParmater.queryData") }}
          </el-button>

          <el-button 
            type="primary" 
            :icon="Refresh" 
            @click="rollCallClick"
            :loading="isRollCallLoading"
            :disabled="!isConnected"
            class="refresh-btn"
          >
            {{ t("message.ClientProjectMonitorSystemMonitorParmater.refreshData") }}
          </el-button>
        </div>
        <div class="pagination">
          <el-pagination
            v-model:current-page="queryParams.pageNum"
            v-model:page-size="queryParams.pageSize"
            :total="queryParams.totalCount"
            :page-sizes="[5, 10, 15, 20, 30]"
            :layout="'total, sizes, prev, pager, next, jumper'"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>

    </div>
  </div>        
</template>

<script setup>
import { ref, reactive, computed, defineExpose, defineProps, watch, onMounted, onUnmounted } from 'vue' 
import { ElMessage, ElMessageBox } from 'element-plus'
import { projectCollectionsDomainVars } from "@/api/ProjectManger";
import { useI18n } from "vue-i18n";
//import baseConfig from "@/public/config.json";
import { getConfig } from '@/utils/configTool';
import { useSignalR } from "@/utils/signalR";
import { getToken } from "@/utils/auth";
import { heart } from "@/api/UserManager";
import { rollCall } from "@/api/DeviceManagement";
import ClientProjectVariableSelector from "@/components/views/ClientProjectVariableSelector.vue";

const { t } = useI18n();

// SignalR 配置
//const url = `${baseConfig.apiBaseUrl}/subscriptionHub`;
const url = ref('');
const isRollCallLoading = ref(false); // 点名刷新加载状态
const isQeuryLoading = ref(false);    // 查询加载状态
let timer = null;
const refreshInterval = 30 * 1000; // 30秒刷新一次数据
const refreshHeartInterval = 10 * 60 * 1000; // 十分钟刷新一次数据
let heartTimer = null;

// 存储键名
const getStorageKey = () => {
  return `selectedVariables_${props.projectId}_${props.collectionId}_${props.domainMenuId}_${props.domainId}`;
};

// 修改：selectedVariables 现在存储对象数组，从本地存储初始化
const selectedVariables = ref([]);

const {
  isConnected,  
  isReconnecting,
  reconnectAttempts,
  reconnectDelay,
  start,
  stop,
  invoke,
  on,
  off,
  onConnected,
} = useSignalR(url, {
  accessTokenFactory: () => getToken() || "",
  automaticReconnect: true,
});

// 重连消息
const reconnectMessage = computed(() => {
  return t("message.reaTimePlot.reconnecting", {
    attempts: reconnectAttempts.value,
    seconds: Math.round(reconnectDelay.value / 1000),
  });
});

// 定义 props
const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true
  },
  collectionId: {
    type: [String, Number],
    required: true
  },
  domainMenuId: {
    type: [String, Number],
    required: true
  },
  domainId: {
    type: [String, Number],
    required: true
  }
})
// 使用 props 数据
const currentProjectId = ref(props.projectId);
const currentCollectionId = ref(props.collectionId);
const currentDomainMenuId = ref(props.domainMenuId); 
const currentDomainId = ref(props.domainId); 
// 查询参数：分页相关配置
const queryParams = reactive({
  pageNum: 1,
  pageSize: 20,
});

// 数据相关
const parameterData = ref({
  data: [
    {
      groupName: "",
      parameters: [
        { title: "", value: 0,setValue:0 }
      ]
    }
  ],
  totalCount: 1
});

// 加载状态控制
const isLoading = ref(false)
const hasLoaded = ref(false)
const lastLoadParams = ref(null)

// 实时数据相关
const realTimeData = ref(new Map()); // 存储变量名到最新值的映射
const isSubscribed = ref(false);



// 3. 异步加载配置函数
async function loadConfig() {
  try {
      // 获取完整配置对象
    const config = await getConfig();
    // 解构使用
    // 4. 赋值 SignalR url
    url.value = config.signalRUrl;
    //console.log('SignalR 地址配置成功：', signalRUrl.value);
  } catch (error) {
    console.error('加载配置文件失败：', error); 
  }
}


// 修改现有的 getPrecision 方法
const getPrecision = (step) => {
  if (!step) return 2;
  
  // 处理浮点数精度问题
  const stepStr = step.toString();
  const decimalIndex = stepStr.indexOf('.');
  
  if (decimalIndex === -1) return 0;
  
  // 限制最大精度为6位，避免过长的小数
  const precision = stepStr.length - decimalIndex - 1;
  return Math.min(precision, 2);
};

 

// 保存选中的变量到本地存储
const saveSelectedVariables = () => {
  try {
    const storageKey = getStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(selectedVariables.value));
    console.log('变量选择已保存到本地存储:', selectedVariables.value);
  } catch (error) {
    console.error('保存变量选择失败:', error);
  }
};

// 从本地存储加载选中的变量
const loadSelectedVariables = () => {
  try {
    const storageKey = getStorageKey();
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        selectedVariables.value = parsed;
        console.log('从本地存储加载变量选择:', selectedVariables.value);
        return true;
      }
    }
  } catch (error) {
    console.error('加载变量选择失败:', error);
  }
  return false;
};

// 清除本地存储的变量选择
const clearSavedVariables = () => {
  try {
    const storageKey = getStorageKey();
    localStorage.removeItem(storageKey);
    console.log('已清除本地存储的变量选择');
  } catch (error) {
    console.error('清除变量选择失败:', error);
  }
};

const handleVariableChange = (newSelection) => {
  try {
    selectedVariables.value = newSelection;
    // 保存到本地存储
    saveSelectedVariables();
    console.log('变量选择已更新并保存:', newSelection);
  } catch (error) {
    ElMessage.error(`${error}`);
  }
};

// 计算属性 - 合并静态数据和实时数据
const mergedPaginatedData = computed(() => {
  if (!parameterData.value?.data?.length) return [];
  
  // 深拷贝原始数据
  //const mergedData = JSON.parse(JSON.stringify(parameterData.value.data));
  const mergedData = parameterData.value.data;
  // 更新实时数据
  mergedData.forEach(group => {
    group.parameters.forEach(param => {
      if (param.varName && param.deviceCode) {
        // 创建唯一的键来查找实时数据
        const uniqueKey = `${param.deviceCode}_${param.varName}`;
        const realTimeValue = realTimeData.value.get(uniqueKey);
        //ElMessage.error(`${param.setValue} mergedPaginatedData`);
        if (realTimeValue) {
          param.value = realTimeValue.value;
          param.updateAt = realTimeValue.timestamp;          
        }
      }
    });
  });  
  return mergedData;
});

// 模拟 C# StringComparer.Ordinal 排序（按 Unicode 编码纯字节比较）
const nameInfoCompare = (nameA, nameB) => {  
  return nameA.localeCompare(nameB, 'en-US', {
    sensitivity: 'variant',
    numeric: false,
    caseFirst: 'upper',
    usage: 'sort'
  });
};

// 优化版本：保持后端排序
const paginatedData = computed(() => {
  if (!mergedPaginatedData.value?.length) return [];
  
  const allParameters = mergedPaginatedData.value.flatMap(group => 
    (group.parameters || []).map(param => {
      // 根据 value 的小数位数计算 step
      const calculateStep = (value) => {
        if (!value && value !== 0) return 0.01; // 默认值
        
        const strValue = value.toString();
        const decimalIndex = strValue.indexOf('.');
        
        if (decimalIndex === -1) {
          // 没有小数，按个位数
          return 1;
        } else {
          // 有小数，根据小数位数计算
          const decimalPlaces = strValue.length - decimalIndex - 1;
          return Math.pow(0.1, decimalPlaces);
        }
      };
      
      return {
        groupName: group.groupName,
        groupSort: group.groupSort || 0,
        varSort: param.varSort || 0,
        title: param.title,
        value: param.value,
        unit: param.unit,
        updateAt: param.updateAt,
        deviceCode: param.deviceCode,
        description: param.description,
        varName: param.varName || '',
        // 确保数值类型正确
        setValue: param.setValue ? Number(param.setValue) : Number(param.value),
        min: Number(param.min) || -1000,
        max: Number(param.max) || 10000,
        // 根据 value 的小数位数动态设置 step
        step: calculateStep(param.value),
        isSetting: param.isSetting || false
      };
    })
  );

  // 排序逻辑保持不变
  const sorted = allParameters.sort((a, b) => {
    if (a.groupSort !== b.groupSort) return a.groupSort - b.groupSort;
    const groupNameCompare = nameInfoCompare(a.groupName, b.groupName);
    if (groupNameCompare !== 0) return groupNameCompare;
    if (a.varSort !== b.varSort) return a.varSort - b.varSort;
    return nameInfoCompare(a.title, b.title);
  });
  
  return sorted;
});

// // 在计算属性中也确保数值类型正确
// const paginatedData = computed(() => {
//   if (!mergedPaginatedData.value?.length) return [];
  
//   const allParameters = mergedPaginatedData.value.flatMap(group => 
//     (group.parameters || []).map(param => ({
//       groupName: group.groupName,
//       groupSort: group.groupSort || 0,
//       varSort: param.varSort || 0,
//       title: param.title,
//       value: param.value,
//       unit: param.unit,
//       updateAt: param.updateAt,
//       deviceCode: param.deviceCode,
//       varName: param.varName || '',
//       // 确保数值类型正确
//       setValue: param.setValue ? Number(param.setValue) : Number(param.value),
//       min: Number(param.min) || -1000,
//       max: Number(param.max) || 10000,
//       step: Number(param.step) || 0.01,
//       //precision: Number(param.precision) || 2,
//       isSetting: param.isSetting || false
//     }))
//   );

//   // 排序逻辑保持不变
//   const sorted = allParameters.sort((a, b) => {
//     if (a.groupSort !== b.groupSort) return a.groupSort - b.groupSort;
//     const groupNameCompare = nameInfoCompare(a.groupName, b.groupName);
//     if (groupNameCompare !== 0) return groupNameCompare;
//     if (a.varSort !== b.varSort) return a.varSort - b.varSort;
//     return nameInfoCompare(a.title, b.title);
//   });
  
//   return sorted;
// });



const groupedPaginatedData = computed(() => {
  const data = paginatedData.value;
  if (!data.length) return [];
  
  const grouped =  Object.values(
    data.reduce((groups, item) => {
      const groupName = item.groupName;      
      if (!groups[groupName]) {
        groups[groupName] = {
          groupName,
          groupSort: item.groupSort,
          parameters: []
        };
      }
      
      groups[groupName].parameters.push({
        title: item.title,
        value: item.value,
        unit: item.unit,
        updateAt:item.updateAt,
        deviceCode:item.deviceCode,
        description: item.description,
        varSort: item.varSort,
        varName: item.varName,
        // 新增设置相关字段
        setValue: item.setValue,
        min: item.min,
        max: item.max,
        step: item.step,
        //precision: item.precision,
        isSetting: item.isSetting
      });
      
      return groups;
    }, {})
  ).sort((a, b) => {
              if (a.groupSort !== b.groupSort) return a.groupSort - b.groupSort;
              return (a.groupName || '').localeCompare(b.groupName || '', 'en', { sensitivity: 'base', ignorePunctuation: false });
            })
            .map(group => ({
              ...group,
              parameters: group.parameters.sort((a, b) => {
                if (a.varSort !== b.varSort) return a.varSort - b.varSort;
                return (a.title || '').localeCompare(b.title || '', 'en', { sensitivity: 'base', ignorePunctuation: false });
              })
            }));
    
  console.log('分组排序结果:', grouped.map(g => ({
    groupName: g.groupName,
    groupSort: g.groupSort
  })));  
  return grouped;
});

// 获取设备统计信息（用于调试）
const getSubscriptionStats = () => {
  const deviceVariableMap = getDeviceVariableMap();
  const stats = {
    totalDevices: deviceVariableMap.size,
    totalVariables: 0,
    devices: {}
  };
  
  for (const [deviceCode, variables] of deviceVariableMap) {
    stats.devices[deviceCode] = variables.length;
    stats.totalVariables += variables.length;
  }
  
  return stats;
}

// 在 loadDefaultParameters 方法中添加统计信息
const loadDefaultParameters = async (varNames = null) => {
  if (isLoading.value) return;
  
  const currentParams = {
    projectId: props.projectId,
    collectionId: props.collectionId,
    domainMenuId: props.domainMenuId,
    domainId: props.domainId,
    pageNum: queryParams.pageNum,
    pageSize: queryParams.pageSize
  };
   
  try {
    isLoading.value = true;
    
    let nameVarFilters = undefined;
    if (varNames && varNames.length > 0) {
        nameVarFilters = varNames.map(item => item.name).filter(Boolean);
    }

    const params = { 
      projectId: props.projectId,      
      collectionsId: props.collectionId,
      domainParentId: props.domainMenuId,
      domainId: props.domainId, 
      companyId: 0,
      groupNames:undefined,
      names:nameVarFilters && nameVarFilters.length > 0 ? nameVarFilters : undefined,    
      name: undefined,       
      display_name: undefined,
      pageNum: queryParams.pageNum,
      pageSize: queryParams.pageSize,
    };

    console.log('加载参数数据:', params);

    const res = await projectCollectionsDomainVars(params);
    if(!res.success) {
      ElMessage.error(res?.msg);  
      return;
    }    
    
    if (!res.data.list || res.data.list.length === 0) {
      parameterData.value = { data: [], totalCount: 0 };
      ElMessage.warning(res.msg);
      return;
    }
    
    parameterData.value = convertToParameterData(res);
    queryParams.totalCount = parameterData.value.totalCount;
    hasLoaded.value = true;
    lastLoadParams.value = currentParams;
    
    // 显示订阅统计信息
    const stats = getSubscriptionStats();
    console.log('订阅统计:', stats);
    
    // 数据加载完成后启动实时订阅
    await startRealTimeSubscription();
    
  } catch (error) {
    console.error('加载业务选项失败:', error);    
    ElMessage.error(error.message || t("message.ClientProjectMonitorSystemMonitorParmater.loadFail"));
  } finally {
    isLoading.value = false;
  }
}

// 启动实时数据订阅
const startRealTimeSubscription = async () => {
  if (!isConnected.value) {
    console.log('SignalR 未连接，等待连接...');
    return;
  }
  
  // 按设备代码分组变量
  const deviceVariableMap = getDeviceVariableMap();
  if (deviceVariableMap.size === 0) {
    console.log('没有可订阅的变量');
    return;
  }
  
  try {
    const token = getToken();
    
    // 为每个设备创建订阅请求
    const subscriptionPromises = [];
    
    for (const [deviceCode, variableNames] of deviceVariableMap) {
      if (variableNames.length === 0) continue;
      
      const params = {
        token: token,
        deviceCode: deviceCode,
        imei: null,
        Dir: "d",
        type: 1, // 实时数据订阅
        timer: new Date().toISOString(),
        priority: 1,
        r_data: variableNames.map(name => ({ name })),
      };
      
      const jsonString = JSON.stringify(params);
      subscriptionPromises.push(
        invoke("RequestData", jsonString)
          .then(() => {
            console.log(`设备 ${deviceCode} 订阅成功，变量数量: ${variableNames.length}`);
          })
          .catch(error => {
            console.error(`设备 ${deviceCode} 订阅失败:`, error);
            throw error;
          })
      );
    }
    
    // 并行发送所有订阅请求
    await Promise.all(subscriptionPromises);
    isSubscribed.value = true;
    console.log('所有设备实时数据订阅已启动', deviceVariableMap);
    
  } catch (error) {
    console.error('启动实时订阅失败:', error);
  }
}

// 按设备代码分组变量
const getDeviceVariableMap = () => {
  const deviceMap = new Map();
  
  if (!parameterData.value?.data?.length) return deviceMap;
  
  parameterData.value.data.forEach(group => {
    group.parameters.forEach(param => {
      if (param.varName && param.deviceCode) {
        if (!deviceMap.has(param.deviceCode)) {
          deviceMap.set(param.deviceCode, []);
        }        
        // 去重
        if (!deviceMap.get(param.deviceCode).includes(param.varName)) {
          deviceMap.get(param.deviceCode).push(param.varName);
        }
      }
    });
  });
  
  return deviceMap;
}

// 停止实时数据订阅
const stopRealTimeSubscription = async () => {
  if (!isConnected.value || !isSubscribed.value) return;
  
  try {
    
    const token = getToken();
    const deviceVariableMap = getDeviceVariableMap();
    
    const releasePromises = [];
    
    for (const [deviceCode] of deviceVariableMap) {
      const params = {
        token: token,
        deviceCode: deviceCode,
        imei: null,
        Dir: "d",
        type: 1,
        priority: 1,
        timer: new Date().toISOString(),
        r_data: [], // 空数组表示取消该设备的所有订阅
      };
      
      const jsonString = JSON.stringify(params);
      
      releasePromises.push(
        invoke("ReleaseData", jsonString)
          .then(() => {
            console.log(`设备 ${deviceCode} 取消订阅成功`);
          })
          .catch(error => {
            console.error(`设备 ${deviceCode} 取消订阅失败:`, error);
          })
      );
    }
    
    // 并行发送所有取消订阅请求
    await Promise.all(releasePromises);
    isSubscribed.value = false;
    console.log('所有设备实时数据订阅已停止');
    
  } catch (error) {
    console.error('停止实时订阅失败:', error);
  }
}

// 处理实时数据消息
const handleRealTimeData = (message) => {
  try {
    const receivedData = JSON.parse(message);
    console.log('收到实时数据:', {
      deviceCode: receivedData.DeviceCode,
      varsCount: receivedData.Vars?.length || 0,
      time: receivedData.time
    });
    
    if (receivedData.Vars && Array.isArray(receivedData.Vars)) {
      let hasUpdate = false;
      
      receivedData.Vars.forEach(variable => {
        if (variable && variable.Name && variable.Error === 0) {
          // 创建唯一的键：deviceCode + variableName
          const uniqueKey = `${receivedData.DeviceCode}_${variable.Name}`;
          
          // 更新实时数据
          realTimeData.value.set(uniqueKey, {
            value: variable.Value?.toString() || '0',
            deviceCode: receivedData.DeviceCode,
            varName: variable.Name,
            timestamp: receivedData.time.replace('T',' ')
          });
          
          hasUpdate = true;
          
          console.log(`更新变量: ${uniqueKey} = ${variable.Value}`);
        }
      });
      
      // 只有有更新时才触发界面刷新
      if (hasUpdate) {
        // 强制触发响应式更新
        parameterData.value = { ...parameterData.value };
      }
    }
  } catch (error) {
    console.error('处理实时数据错误:', error);
    ElMessage.error(`${error.message}`);
  }
}
 
// 获取所有变量的名称列表
const getVariableNames = () => {
  const variableNames = new Set();
  
  if (!parameterData.value?.data?.length) return [];
  
  parameterData.value.data.forEach(group => {
    group.parameters.forEach(param => {
      if (param.varName) {
        variableNames.add(param.varName);
      }
    });
  });
  
  return Array.from(variableNames);
}


// 处理输入框数值变化
// const handleInputChange = (param) => {
//   console.log('输入框数值变化:', param.setValue);
// }


// 修改 handleValueChange 方法
const handleValueChange = (param) => {
  console.log(`参数 ${param.title} 值改变为:`, param.setValue);
  //ElMessage.error(`${param.setValue} handleValueChange`);
    // 重新赋值触发响应式更新
  const newValue = Number(param.setValue);
  //param.setValue = newValue;  
  updateParameterSetValue(param.deviceCode, param.varName, newValue);
}

// 直接更新 parameterData 中的 setValue
const updateParameterSetValue = (deviceCode, varName, newSetValue) => {
  if (!parameterData.value?.data) return;
  
  parameterData.value.data.forEach(group => {
    group.parameters.forEach(param => {
      if (param.deviceCode === deviceCode && param.varName === varName) {
        param.setValue = Number(newSetValue);
        param.userModified = true;
      }
    });
  });
  
  // 强制响应式更新
  parameterData.value = { ...parameterData.value };
}


// 在数据转换方法中确保 setValue 正确初始化
const convertToParameterData = (res) => {
  if (!res?.data?.list) {
    return {
      data: [],
      totalCount: 0
    };
  }

  return {
    data: res.data.list.map(item => ({
      groupName: item.group_name || '未分组',
      groupSort: item.group_sort || 0,
      parameters: (item.list_parm || []).map(param => ({
        varName: param.var_name,
        title: param.display_name,
        value: param.var_current_value?.toString() || '0',
        unit: param.var_unit?.toString() || '',
        varSort: param.var_sort || 0,
        deviceCode: param.device_code || '',
        updateAt: param.update_at.replace('T', ' ') || '',
        description: param.description,
        // 确保 setValue 正确初始化
        setValue: Number(param.var_set_value) || 0, // 确保是数字类型
        min: Number(param.min_value) || -1000,
        max: Number(param.max_value) || 1000,
        step: Number(param.step_value) || 0.01,
        //precision: Number(param.precision) || 2,
        isSetting: false
      })).filter(param => param.title !== '未知参数')
    })).filter(group => group.parameters.length > 0),
    totalCount: res.data.totalCount || 0
  };
};


// 设置参数值

const handleSetValue = async (param) => {
  try {
    // 确认对话框 - 中英文
    await ElMessageBox.confirm(
      t("message.ClientDeviceMonitorSystemDebugHostDebug.confirmSetValue", {
        title: param.title,
        value: param.setValue,
        unit: param.unit || ''
      }),
      t("message.ClientDeviceMonitorSystemDebugHostDebug.confirmSet"),
      {
        confirmButtonText: t("message.common.confirm"),
        cancelButtonText: t("message.common.cancel"),
        type: 'warning'
      }
    );

    param.isSetting = true;
     
    if (!isConnected.value) {
      ElMessage.error(t("message.ClientDeviceMonitorSystemDebugHostDebug.connectError"));
      return;
    }

    // 获取当前页面的所有变量
    const currentVariables = getVariableNames();
    if (currentVariables.length === 0) {
      ElMessage.warning(t("message.ClientDeviceMonitorSystemDebugHostDebug.noVariables"));
      return;
    }

    const token = getToken();
    const deviceVariableMap = getDeviceVariableMap();
    
    if (deviceVariableMap.size === 0) {
      ElMessage.warning(t("message.ClientDeviceMonitorSystemDebugHostDebug.noDevices"));
      return;
    }

    const params = [{
      token: token,
      deviceCode: param.deviceCode,
      imei: undefined,
      Dir: "Down",
      type: 4, // 点名类型
      timer: new Date().toISOString(),
      priority: 1,
      r_data: [],
      w_data: [],
    }];
  
    params[0].r_data.push({ name: param.varName });                 
    params[0].w_data.push({name: param.varName, value: param.setValue})

    await rollCall(params)
      .then(() => {
        console.log(`设备 ${param.deviceCode} 点名成功，变量数量: 1`);
        // 设置成功提示 - 中英文
        ElMessage.success(
          t("message.ClientDeviceMonitorSystemDebugHostDebug.setSuccess", {
            title: param.title
          })
        );
      })
      .catch(error => {
        console.error(`设备 ${param.deviceCode} 点名失败:`, error);
        throw error;
      });

    // 点名后重新订阅实时数据
    await startRealTimeSubscription();

  } catch (error) {
    if (error !== 'cancel') {
      console.error('设置参数值失败:', error);
      // 设置失败提示 - 中英文
      ElMessage.error(
        t("message.ClientDeviceMonitorSystemDebugHostDebug.setFailed", {
          title: param.title,
          error: error.message
        })
      );
    }
  } finally {
    param.isSetting = false;
  }
} 



// 优化后的点名刷新方法
const rollCallClick = async () => {
  if (!isConnected.value) {
    ElMessage.error(t("message.ClientProjectMonitorSystemMonitorParmater.connectError"));
    return;
  }

  // 获取当前页面的所有变量
  const currentVariables = getVariableNames();
  if (currentVariables.length === 0) {
    ElMessage.warning(t("message.ClientProjectMonitorSystemMonitorParmater.noVariables"));
    return;
  }

  try {
    isRollCallLoading.value = true;
    
    const token = getToken();
    const deviceVariableMap = getDeviceVariableMap();
    
    if (deviceVariableMap.size === 0) {
      ElMessage.warning(t("message.ClientProjectMonitorSystemMonitorParmater.noDevices"));
      return;
    }

    // 为每个设备发送点名请求
    const rollCallPromises = [];
    
    for (const [deviceCode, variableNames] of deviceVariableMap) {
      if (variableNames.length === 0) continue;
      
      // 将变量名按每5个分组
      const variableChunks = chunkArray(variableNames, 5);
      
      // 为每个变量包创建请求
      for (let i = 0; i < variableChunks.length; i++) {
        const chunk = variableChunks[i];
        
        const params = {
          token: token,
          deviceCode: deviceCode,
          imei: undefined,
          Dir: "Down",
          type: 4, // 点名类型
          timer: new Date().toISOString(),
          priority: 1,
          r_data: chunk.map(name => ({ name })),
          w_data: [],
        };
        
        rollCallPromises.push(
          rollCall([params])
            .then(() => {
              console.log(`设备 ${deviceCode} 第${i + 1}/${variableChunks.length}包点名成功，变量数量: ${chunk.length}`);
            })
            .catch(error => {
              console.error(`设备 ${deviceCode} 第${i + 1}包点名失败:`, error);
              throw error;
            })
        );
        
        // 可选：添加延迟避免请求过于密集
        if (i < variableChunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }
    
    // 并行发送所有点名请求
    await Promise.all(rollCallPromises);
    
    ElMessage.success(
      t("message.ClientProjectMonitorSystemMonitorParmater.rollCallSuccess", {
        deviceCount: deviceVariableMap.size,
        variableCount: currentVariables.length
      })
    );
    
    // 点名后重新订阅实时数据
    await startRealTimeSubscription();
    
  } catch (error) {
    if (error !== 'cancel') {
      console.error('点名操作失败:', error);
      ElMessage.error(
        error.response?.data?.message || 
        error.message || 
        t("message.ClientProjectMonitorSystemMonitorParmater.rollCallError")
      );
    }
  } finally {
    isRollCallLoading.value = false;
  }
}

// 查询数据方法
const handleQueryDataClick = async () => {
  try {
    isQeuryLoading.value = true;
    queryParams.pageNum = 1;
    await loadDefaultParameters(selectedVariables.value);
  } catch (error) {
    console.error('查询操作失败:', error);
  } finally {
    isQeuryLoading.value = false;
  }
}

// 辅助函数：将数组按指定大小分块
const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

// 分页事件
const handleSizeChange = async(newSize) => {
  queryParams.pageSize = newSize;
  queryParams.pageNum = 1;
  await loadDefaultParameters(selectedVariables.value);
};

const handleCurrentChange = async (newPage) => {
  queryParams.pageNum = newPage;
  await loadDefaultParameters(selectedVariables.value);
  document.querySelector('.data-grid')?.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

// 标签切换事件
const handleTabChange = async (name) => {
  console.log('切换到标签:', name);
  queryParams.pageNum = 1;
  await loadDefaultParameters(selectedVariables.value);
};

// 连接成功回调
const unsubscribeConnected = onConnected(async () => {
  console.log('SignalR 连接成功');
  if (hasLoaded.value) {
    await startRealTimeSubscription();
  }
});

// 监听 props 变化
watch(
  [
    () => props.projectId,
    () => props.collectionId, 
    () => props.domainMenuId,
    () => props.domainId
  ],
  async (newValues, oldValues) => {
    const [newProjectId, newCollectionId, newDomainMenuId, newDomainId] = newValues;
    const [oldProjectId, oldCollectionId, oldDomainMenuId, oldDomainId] = oldValues || [];
    
    if (newProjectId && newCollectionId && newDomainMenuId && newDomainId) {
      const isPropsChanged = 
        newProjectId !== oldProjectId || 
        newCollectionId !== oldCollectionId || 
        newDomainMenuId !== oldDomainMenuId || 
        newDomainId !== oldDomainId;
      
      if (!oldValues || isPropsChanged) {
        try {
          // 停止旧的订阅
          await stopRealTimeSubscription();
          realTimeData.value.clear();
          
          // 重新加载保存的变量选择
          loadSelectedVariables();
          
          await loadDefaultParameters(selectedVariables.value);
        } catch (error) {
          console.error('监听props变化时加载数据失败:', error);
          ElMessage.error(error);
        }
      }
    }
  },
  { immediate: true }
);

// 开始自动刷新
const startAutoRefresh = () => {
  stopAutoRefresh();
  //首次订阅
  startRealTimeSubscription();

  timer = setInterval(() => {    
      startRealTimeSubscription();    
  }, refreshInterval);
  //启动心跳
  heartTimer = setInterval(() => {
    heart();
  }, refreshHeartInterval);
};

// 停止自动刷新
const stopAutoRefresh = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }

  if (heartTimer) {
    clearInterval(heartTimer);
    heartTimer = null;
  }
};

// 生命周期
onMounted(async () => {
  try {
    await loadConfig();
    // 首先加载保存的变量选择
    loadSelectedVariables();
    
    await start();
    on("ReceiveMessage", handleRealTimeData);
    startAutoRefresh();
    
    // 使用保存的变量进行初始加载
    if (selectedVariables.value.length > 0) {
      await loadDefaultParameters(selectedVariables.value);
    } else {
      await loadDefaultParameters();
    }
  } catch (error) {
    console.error('SignalR 启动失败:', error);
  }
});

onUnmounted(async () => {
  try {
    stopRealTimeSubscription();
    stopAutoRefresh();
    off("ReceiveMessage", handleRealTimeData);
    unsubscribeConnected();
    stop();
  } catch (error) {
    console.error('清理失败:', error);
  }
});

// 暴露方法给父组件
defineExpose({
  handleTabChange,
  saveSelectedVariables,
  loadSelectedVariables,
  clearSavedVariables
});
</script>

<style scoped>
/* 基础动画 */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 连接状态 */
.connection-status {
  margin-bottom: 16px;
  animation: fadeIn 0.5s;
}

/* 主容器 */
.debug-parameter-settings-container {
  padding: 0px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  width: 100%;
  height: 85vh;
  overflow: hidden;
  box-sizing: border-box;
  color: #fff;
  padding: 5px;  
}

.content-wrapper {
  height: 100%;
  overflow: hidden;  
  display: flex;  
  flex-direction: column;
  gap: 10px;
}

.group-container {
  flex: 1;
  height: 100%;
  overflow: auto;  
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-right: 8px;
}

/* 滚动条美化 */
.group-container::-webkit-scrollbar {
  width: 8px;
}

.group-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.group-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

.group-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* 分组标题样式 */
.group-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* 数据网格布局 */
.data-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

/* 数据卡片样式优化 - 减小高度 */
.data-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.1));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px; /* 减小内边距 */
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px; /* 减小间距 */
  min-height: 140px; /* 减小最小高度 */
}

.data-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
}

.data-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.15));
  border-color: rgba(255, 255, 255, 0.3);
}

/* 优化卡片头部布局 */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0;
  padding-bottom: 6px; /* 减小内边距 */
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 36px; /* 减小最小高度 */
}

.title-section {
  flex: 1;
  min-width: 0;
  max-width: calc(100% - 75px); /* 稍微调整时间区域空间 */
}

.main-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 2px; /* 减小底部边距 */
}

.param-title {
  font-size: 14px; /* 稍微减小字体 */
  font-weight: 600;
  color: #e6f7ff;
  line-height: 1.3;
}

.param-varname {
  font-size: 10px; /* 稍微减小字体 */
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
  font-style: italic;
}

/* 优化description显示 */
.param-description {
  display: flex;
  align-items: center;
  gap: 3px; /* 减小间距 */
  font-size: 10px; /* 稍微减小字体 */
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.2; /* 调整行高 */
  margin-top: 1px; /* 减小顶部边距 */
  cursor: help;
  max-width: 100%;
  transition: color 0.3s ease;
  padding: 1px 3px; /* 减小内边距 */
  border-radius: 3px;
}

.param-description:hover {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.1);
}

.description-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.description-icon {
  font-size: 11px; /* 稍微减小图标 */
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
  transition: color 0.3s ease;
}

.param-description:hover .description-icon {
  color: #4facfe;
  transform: scale(1.1);
}

/* 自定义 Tooltip 样式 */
:deep(.custom-description-tooltip) {
  max-width: 300px;
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.95) 0%, rgba(42, 82, 152, 0.95) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(79, 172, 254, 0.3);
  border-radius: 8px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  color: #fff;
  padding: 12px;
  font-size: 12px;
  line-height: 1.5;
}

:deep(.custom-description-tooltip .el-popper__arrow) {
  display: none;
}

:deep(.custom-description-tooltip .el-popper__arrow::before) {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.95) 0%, rgba(42, 82, 152, 0.95) 100%);
  border: 1px solid rgba(79, 172, 254, 0.3);
}

.time-section {
  flex-shrink: 0;
  margin-left: 6px; /* 减小边距 */
}

.update-time {
  display: flex;
  align-items: center;
  gap: 3px; /* 减小间距 */
  font-size: 9px; /* 减小字体 */
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
}

.update-time i {
  font-size: 10px; /* 减小图标 */
}

/* 优化数值和控制区域 */
.value-control-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px; /* 减小间距 */
  align-items: stretch; /* 让两个区域等高 */
  margin-top: 4px; /* 减小与上面线的距离 */
}

.current-value-display,
.set-value-control {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 8px; /* 减小内边距 */
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: center; /* 垂直居中内容 */
  min-height: 60px; /* 设置最小高度，让两个框一样高 */
}

.value-label,
.set-value-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px; /* 调整标签与内容的间距 */
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center; /* 居中显示标签 */
}

.value-content {
  display: flex;
  align-items: baseline;
  gap: 4px;
  justify-content: center; /* 居中显示数值 */
}

.value-number {
  font-size: 16px; /* 稍微减小字体 */
  font-weight: 700;
  color: #4facfe;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.value-unit {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.control-row {
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center; /* 居中控制元素 */
}

.set-value-input {
  flex: 1;
  min-width: 0;
}

.set-button {
  flex-shrink: 0;
  white-space: nowrap;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border: none;
  font-weight: 500;
  height: 26px; /* 稍微减小高度 */
  padding: 0 10px; /* 减小内边距 */
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.set-button:hover:not(.is-disabled) {
  background: linear-gradient(135deg, #3a9bf7 0%, #00d9f2 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 172, 254, 0.3);
}

.set-button.is-disabled {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.4);
  cursor: not-allowed;
}

/* 分页区域样式 */
.pagination-container {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 10px 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  gap: 20px;
}

.pagination-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
}

.selector-wrapper {
  flex: 1;
  min-width: 200px;
  max-width: 300px; 
}

.variable-selector:deep(.selector-header) {
  height: 32px;
  min-height: 32px;
  line-height: 32px;
  padding: 0 12px;
  display: flex;
  align-items: center;
}

.refresh-btn {
  flex-shrink: 0;
  white-space: nowrap;
}

/* 确保选择器组件内部样式正确 */
:deep(.project_variable-selector-container) {
  width: 100%;
}

:deep(.selector-header) {
  height: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
}

/* 分页区域 */
.pagination {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

/* Element Plus 分页组件样式重写 */
:deep(.el-pagination) {
  color: #fff;
  justify-content: flex-end;
  flex-wrap: wrap;
}

:deep(.el-pagination__total) {
  color: #fff !important;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

:deep(.el-pagination__jump) {
  color: #fff !important;
  font-weight: 500;
}

:deep(.el-pagination button) {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  border-radius: 6px;
  transition: all 0.3s ease;
}

:deep(.el-pagination button:hover) {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next) {
  background: rgba(255, 255, 255, 0.1);
}

:deep(.el-pagination .el-pager li) {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  border-radius: 6px;
  margin: 0 4px;
  transition: all 0.3s ease;
}

:deep(.el-pagination .el-pager li:hover) {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
}

:deep(.el-pagination .el-pager li.is-active) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-color: #4facfe;
  color: #fff;
  font-weight: 600;
}

:deep(.el-pagination .el-select .el-input__inner) {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  border-radius: 6px;
}

:deep(.el-pagination .el-select .el-input__suffix .el-icon) {
  color: #fff;
}

:deep(.el-pagination .el-input__inner::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}

:deep(.el-pagination .jumper .el-input__inner) {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  border-radius: 6px;
}

/* Element Plus 输入框组件样式 */
.set-value-input:deep(.el-input-number) {
  width: 100%;
}

.set-value-input:deep(.el-input-number .el-input__inner) {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  text-align: center;
  font-size: 12px; /* 减小字体 */
  height: 26px; /* 减小高度 */
  line-height: 26px;
}

.set-value-input:deep(.el-input-number .el-input-number__decrease),
.set-value-input:deep(.el-input-number .el-input-number__increase) {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
  width: 20px; /* 减小宽度 */
}

.set-value-input:deep(.el-input-number .el-input-number__decrease:hover),
.set-value-input:deep(.el-input-number .el-input-number__increase:hover) {
  background: rgba(255, 255, 255, 0.25);
}

/* 卡片悬停效果优化 */
.data-card:hover .value-number {
  color: #00f2fe;
  transition: color 0.3s ease;
}

.data-card:hover .current-value-display,
.data-card:hover .set-value-control {
  border-color: rgba(255, 255, 255, 0.2);
  transition: border-color 0.3s ease;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .pagination-container {
    flex-direction: column;
    gap: 12px;
  }
  
  .pagination-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .selector-wrapper {
    max-width: none;
    flex: 1;
  }
  
  .pagination {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .value-control-section {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .card-header {
    flex-direction: column;
    gap: 6px;
  }
  
  .time-section {
    margin-left: 0;
    align-self: flex-end;
  }
  
  .control-row {
    flex-direction: column;
  }
  
  .set-button {
    width: 100%;
    justify-content: center;
  }

  .pagination-actions {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
  
  .selector-wrapper {
    width: 100%;
    max-width: none;
  }
  
  .refresh-btn {
    width: 100%;
  }
  
  .pagination-container {
    padding: 8px 12px;
  }
  
  .data-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .main-title {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .param-varname {
    margin-left: 0;
  }
  
  .value-content {
    justify-content: center;
  }
  
  .current-value-display,
  .set-value-control {
    padding: 6px;
    min-height: 55px;
  }
  
  .data-card {
    padding: 10px;
    min-height: 130px;
  }
  
  .param-title {
    font-size: 13px;
  }
  
  .value-number {
    font-size: 15px;
  }
  
  .param-description:hover::after {
    width: 150px;
    font-size: 11px;
    padding: 6px 8px;
  }
}
</style>